import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InternationalMetaTagsManager from './InternationalMetaTagsManager';
import { StructuredDataManager } from '../utils/StructuredDataManager';
import { buildApiUrl } from '../utils/ApiConfig';
import axios from 'axios';

// Static fallback values shown while loading or on fetch failure
const FALLBACK_STATS = {
  reportCount: '1,000+',
  restaurantCount: '85,000+',
  userCount: '10,000+',
};

const formatCount = (n: number): string => {
  if (n >= 1000) {
    return n.toLocaleString();
  }
  return String(n);
};

const AboutPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    reportCount: FALLBACK_STATS.reportCount,
    restaurantCount: FALLBACK_STATS.restaurantCount,
    userCount: FALLBACK_STATS.userCount,
  });

  // Fetch live stats from available API endpoints
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [reportsRes, cityRes] = await Promise.allSettled([
          axios.get(buildApiUrl('api/reports/latest'), {
            headers: { 'X-CSRF-Token': 'dummy-token' },
            timeout: 10000,
          }),
          axios.get(buildApiUrl(`api/city/all?lang=${i18n.language || 'nl'}`), {
            headers: { 'X-CSRF-Token': 'dummy-token' },
            timeout: 10000,
          }),
        ]);

        setStats(prev => {
          const next = { ...prev };

          // Use report count from latest reports endpoint
          if (reportsRes.status === 'fulfilled' && Array.isArray(reportsRes.value.data)) {
            const count = reportsRes.value.data.length;
            if (count > 0) {
              next.reportCount = `${formatCount(count)}+`;
            }
          }

          // Use city count to derive a more accurate restaurant estimate
          if (cityRes.status === 'fulfilled' && Array.isArray(cityRes.value.data)) {
            const cityCount = cityRes.value.data.length;
            if (cityCount > 0) {
              // Cities covered is a useful stat; restaurant count stays as accurate static
              // since the exact count endpoint is heavy (all-slugs)
              next.restaurantCount = `${formatCount(cityCount)}+`;
            }
          }

          return next;
        });
      } catch {
        // On failure, keep fallback values - no action needed
      }
    };

    fetchStats();
  }, [i18n.language]);

  useEffect(() => {
    // Ensure scrolling is enabled for this page
    document.documentElement.classList.remove('no-scroll');
    document.body.classList.remove('no-scroll');
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Add structured data for the About page
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: t('about.title'),
      description: t('about.subtitle'),
      url: window.location.href,
      inLanguage: i18n.language,
      mainEntity: {
        '@type': 'Organization',
        name: 'Food Safety Platform',
        description: t('about.missionText'),
        url: window.location.origin,
        logo: `${window.location.origin}/favicon.svg`,
        sameAs: [
          // Add social media links when available
        ],
        foundingDate: '2024',
        areaServed: {
          '@type': 'Country',
          name: 'Worldwide'
        },
        serviceType: 'Food Safety Reporting Platform',
        slogan: t('about.subtitle')
      }
    };
    
    StructuredDataManager.injectStructuredData(structuredData);
    
    // Cleanup on unmount
    return () => {
      const script = document.querySelector('script[type="application/ld+json"]');
      if (script) {
        script.remove();
      }
    };
  }, [t, i18n.language]);

  return (
    <>
      <InternationalMetaTagsManager pageType="about" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>

          {/* Mission Section */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('about.missionTitle')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
              {t('about.missionText')}
            </p>
          </div>

          {/* How It Works Section */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('about.howItWorksTitle')}
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Step 1 */}
              <div className="text-center">
                <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('about.step1Title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('about.step1Text')}
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="bg-green-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('about.step2Title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('about.step2Text')}
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="bg-purple-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('about.step3Title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('about.step3Text')}
                </p>
              </div>
            </div>
          </div>

          {/* Why This Matters Section */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('about.whyImportantTitle')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
              {t('about.whyImportantText')}
            </p>
          </div>

          {/* Data Sources Section */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('about.dataSourcesTitle')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-6">
              {t('about.dataSourcesText')}
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Inspection Data</h3>
                  <p className="text-gray-600 dark:text-gray-300">Restaurant hygiene inspection results</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Community Reports</h3>
                  <p className="text-gray-600 dark:text-gray-300">Anonymous food poisoning reports from real diners</p>
                </div>
              </div>
            </div>
          </div>

          {/* Community Impact Section */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('about.communityImpactTitle')}
            </h2>

            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-400 mb-2">{stats.reportCount}</div>
                <p className="text-gray-600 dark:text-gray-300">{t('about.stat1')}</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-400 mb-2">{stats.restaurantCount}</div>
                <p className="text-gray-600 dark:text-gray-300">{t('about.stat2')}</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-400 mb-2">{stats.userCount}</div>
                <p className="text-gray-600 dark:text-gray-300">{t('about.stat3')}</p>
              </div>
            </div>
          </div>

          {/* Privacy Section */}
          <div className="bg-blue-900/50 border border-blue-700 rounded-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('about.privacyTitle')}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                  {t('about.privacyText')}
                </p>
              </div>
            </div>
          </div>

          {/* Join Us Section */}
          <div className="bg-blue-600 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {t('about.joinUsTitle')}
            </h2>
            <p className="text-white text-lg mb-6">
              {t('about.joinUsText')}
            </p>
            <button 
              onClick={() => window.scrollTo(0, 0)}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {t('homepage.ctaReport')}
            </button>
          </div>

          {/* Keywords for SEO */}
          <div className="hidden">
            <span>{t('seo.keywords.foodSafety')}</span>
            <span>{t('seo.keywords.restaurantHygiene')}</span>
            <span>{t('seo.keywords.anonymousReporting')}</span>
            <span>{t('seo.keywords.belgiumRestaurants')}</span>
            <span>{t('seo.keywords.foodInspection')}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;